import React, { useState, useEffect, useCallback } from 'react';
import Plot from 'react-plotly.js';
import Plotly from 'plotly.js-dist-min';
import {
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Grid
} from '@mui/material';

const PlotlyFileRenderer = ({ url }) => {
    const [plotData, setPlotData] = useState(null);
    const [error, setError] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(0);

    useEffect(() => {
        const fetchPlotData = async () => {
            try {
                const response = await fetch(url);
                const jsonData = await response.json();
                setPlotData(jsonData);
            } catch (error) {
                console.error('Error fetching plotly data:', error);
                setError(error);
            }
        };

        fetchPlotData();
    }, [url]);

    const handleVariantChange = useCallback((event) => {
        const newVariant = event.target.value;
        setSelectedVariant(newVariant);

        setTimeout(() => {
            if (plotData?.results?.[newVariant]?.figures) {
                plotData.results[newVariant].figures.forEach((figure) => {
                    const plotDiv = document.getElementById(`plot-${figure.id}`);
                    if (plotDiv) {
                        Plotly.relayout(plotDiv, {
                            'autosize': true,
                            'width': '100%',
                            'height': '400px',
                            'margin': { l: 50, r: 50, t: 50, b: 50 }, // Match layout margins
                            'padding': 16 // Match the style padding
                        });
                    }
                });
            }
        }, 100);
    }, [plotData]);

    if (error) return <Typography color="error">Error loading plot</Typography>;
    if (!plotData) return <Typography>Loading plot...</Typography>;

    if (!plotData.results || plotData.results.length === 0) {
        return <Typography>No data available</Typography>;
    }

    const currentVariant = plotData.results[selectedVariant];

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            {/* Variant Selector */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Variant</InputLabel>
                <Select
                    value={selectedVariant}
                    label="Select Variant"
                    onChange={handleVariantChange}
                >
                    {plotData.results.map((variant, index) => (
                        <MenuItem key={index} value={index}>
                            Variant {index + 1}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Variant Parameters */}
            <Typography variant="h6" sx={{ mb: 1 }}>
                Variant Parameters
            </Typography>
            <Typography>
                Lower Bound: {currentVariant.variant_params.lb}
                &nbsp;|&nbsp;
                Upper Bound: {currentVariant.variant_params.ub}
            </Typography>

            {/* Variant Statistics */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Variant Statistics
            </Typography>
            {Object.entries(currentVariant.variant_stats).map(([key, value]) => (
                <Typography key={key}>
                    {key}: {value}
                </Typography>
            ))}

            {/* Plotly Figures */}
            <Grid container spacing={3} sx={{ mt: 2, px: 1 }}> {/* Added px: 1 for overall container padding */}
                {currentVariant.figures.map((figure, index) => (
                    <Grid
                        item
                        xs={12}
                        key={`${figure.id}_grid`}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '400px',
                            py: 2, // Add vertical padding between charts
                            '& > div': {
                                boxShadow: 1, // Optional: adds a subtle shadow
                                borderRadius: 2, // Optional: rounds the corners
                            }
                        }}
                    >
                        <Plot
                            divId={`plot-${figure.id}`}
                            data={figure.data}
                            layout={{
                                ...figure.layout,
                                autosize: true,
                                width: '100%',
                                height: 400,
                                margin: { l: 50, r: 50, t: 50, b: 50 }, // Adjust plot internal margins
                            }}
                            config={{
                                responsive: true,
                                useResizeHandler: true,
                                style: { width: '100%', height: '100%' }
                            }}
                            style={{
                                width: '100%',
                                height: '400px',
                                padding: '16px', // Add padding inside the plot
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default PlotlyFileRenderer;